"use client";

import { useRouter } from "next/navigation";
import GuidedActivityCard from "../GuidedActivityCard";
import SectionHeader from "../SectionHeader";
import { useFetch } from "@/hooks/useFetch";
import { ActivityCardSkeleton } from "../ActivityCardSkeleton";
import { getActivitiesByCityRecommendation } from "@/services/activities-service";
import { useEffect, useState } from "react";

interface RecommendedCityActivity {
  title: string;
  description: string;
  activities: Activity[];
  region: string;
  country: string;
  category: string;
}

export default function ActivitiesRecommendedByLocation({
  propActivities,
  city,
  noOfRows = 4,
}: {
  propActivities?: RecommendedCityActivity[];
  city: string;
  noOfRows?: number;
}) {
  const router = useRouter();
  const [activities, setActivities] = useState<RecommendedCityActivity[]>(
    propActivities || [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // const { data: nearMeData, loading } = useFetch(
  //   () => getActivitiesByCityRecommendation(1,{ city }),
  //   []
  // );

  useEffect(() => {
    const fetchActivities = async () => {
      if (propActivities && propActivities?.length > 0) {
        return;
      }
      setLoading(true);
      setError(null);

      if (!city) {
        setLoading(false);
        return;
      }

      try {
        const data = await getActivitiesByCityRecommendation(1, {
          region: city,
        });
        // console.log(data?.data);
        setActivities(data?.data || []);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [city]);

  // console.log(activities);

  // Shuffle helper
  const shuffleArray = <T,>(arr: T[]): T[] =>
    [...arr].sort(() => 0.5 - Math.random());

  if (!loading && (!activities || activities.length === 0)) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ActivityCardSkeleton count={4} />
        </div>
      )}

      {!loading &&
        activities &&
        activities.length > 0 &&
        activities.map((group, groupIndex) => {
          const { title, description, activities, region, category } = group;
          if (!activities || activities.length === 0) return null;

          const displayActivities = shuffleArray(activities).slice(0, noOfRows);

          return (
            <div key={groupIndex} className="mb-14">
              <SectionHeader
                title={title || "Activities Near You"}
                subtitle={
                  description || "Explore activities happening around you"
                }
                onClickFunc={() =>
                  router.push(
                    `/explore/query?city=${
                      region || city
                    }&source=recommendedByLocation&category=${category}`,
                  )
                }
                actionText="See All"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                {displayActivities.map((activity, i) => (
                  <GuidedActivityCard key={i} activity={activity} />
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
}
